import React, { useMemo, useState } from 'react';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { Mail, MapPin, Phone, RefreshCw, Send } from 'lucide-react';
import './ContactSection.css';

const DEFAULT_TAG = 'clevory academy';

const trainings = [
	{ value: '', label: 'Choisir une formation' },
	{ value: 'IT SALES', label: 'IT Sales' },
	{ value: 'AGENTIC AI', label: 'Agentic IA' },
	{ value: 'FORMATION À LA CARTE', label: 'Formation à la carte' },
];

const initialForm = {
	training: '',
	fullName: '',
	email: '',
	phone: '',
	message: '',
	captchaInput: '',
};

function generateCaptcha() {
	const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
	return Array.from(
		{ length: 4 },
		() => chars[Math.floor(Math.random() * chars.length)],
	).join('');
}

export default function ContactSection() {
	const [form, setForm] = useState(initialForm);
	const [errors, setErrors] = useState({});
	const [captcha, setCaptcha] = useState(generateCaptcha());
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [submitState, setSubmitState] = useState({
		type: '',
		message: '',
	});

	const mapUrl = useMemo(
		() =>
			'https://www.google.com/maps?q=Tunis+clevory+academy&z=15&output=embed',
		[],
	);

	const handleChange = (field, value) => {
		setForm((prev) => ({ ...prev, [field]: value }));
		setErrors((prev) => ({ ...prev, [field]: '' }));
		setSubmitState({ type: '', message: '' });
	};

	const refreshCaptcha = () => {
		setCaptcha(generateCaptcha());
		setForm((prev) => ({ ...prev, captchaInput: '' }));
		setErrors((prev) => ({ ...prev, captchaInput: '' }));
	};

	const validate = () => {
		const nextErrors = {};

		if (!form.training.trim()) {
			nextErrors.training = 'Veuillez choisir une formation.';
		}

		if (!form.fullName.trim()) {
			nextErrors.fullName = 'Le nom et prénom sont obligatoires.';
		} else if (form.fullName.trim().length < 3) {
			nextErrors.fullName = 'Veuillez saisir un nom valide.';
		}

		if (!form.email.trim()) {
			nextErrors.email = "L'adresse e-mail est obligatoire.";
		} else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
			nextErrors.email = 'Veuillez saisir une adresse e-mail valide.';
		}

		if (!form.phone) {
			nextErrors.phone = 'Le numéro de téléphone est obligatoire.';
		} else if (!isValidPhoneNumber(form.phone)) {
			nextErrors.phone = 'Veuillez saisir un numéro de téléphone valide.';
		}

		if (!form.captchaInput.trim()) {
			nextErrors.captchaInput = 'Veuillez saisir le captcha.';
		} else if (
			form.captchaInput.trim().toUpperCase() !== captcha.toUpperCase()
		) {
			nextErrors.captchaInput = 'Captcha incorrect.';
		}

		setErrors(nextErrors);
		return Object.keys(nextErrors).length === 0;
	};

	const resetForm = () => {
		setForm(initialForm);
		setCaptcha(generateCaptcha());
		setErrors({});
	};

	const handleSubmit = async (e) => {
		e.preventDefault();

		if (!validate()) return;

		setIsSubmitting(true);
		setSubmitState({ type: '', message: '' });

		const apiUrl =
			process.env.REACT_APP_API_URL ||
			'https://portal.clevory.info/testwordpress';

		const payload = {
			params: {
				name: form.fullName.trim(),
				email: form.email.trim(),
				phone: form.phone.trim(),
				parcours: form.training,
				message: form.message.trim() || false,
				tag_ids: [DEFAULT_TAG],
			},
		};

		try {
			const response = await fetch(apiUrl, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Accept: 'application/json',
				},
				body: JSON.stringify(payload),
			});

			let data = {};
			try {
				data = await response.json();
			} catch {
				data = {};
			}

			if (!response.ok || data.success === false) {
				throw new Error(
					data.error || data.message || "Erreur lors de l'envoi.",
				);
			}

			resetForm();
			setSubmitState({
				type: 'success',
				message: 'Votre message a été envoyé avec succès.',
			});
		} catch (error) {
			console.error('Erreur envoi contact:', error);
			setSubmitState({
				type: 'error',
				message:
					error.message ||
					'Une erreur est survenue. Veuillez réessayer plus tard.',
			});
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<section className='contact-section'>
			<div className='contact-container'>
				<div className='contact-card'>
					<div className='contact-form-side'>
						<div className='contact-header'>
							<p className='contact-label'>Contact</p>
							<h2 className='contact-title'>
								Demandez plus d&apos;informations
							</h2>
							<p className='contact-subtitle'>
								Remplissez le formulaire ci-dessous et notre équipe vous
								contactera rapidement.
							</p>
						</div>

						<form onSubmit={handleSubmit} className='contact-form' noValidate>
							<div className='form-group'>
								<div className='select-wrapper'>
									<select
										value={form.training}
										onChange={(e) => handleChange('training', e.target.value)}
										className={`form-control form-select ${
											errors.training ? 'is-error' : ''
										}`}>
										{trainings.map((training) => (
											<option
												key={training.value || training.label}
												value={training.value}>
												{training.label}
											</option>
										))}
									</select>

									<span className='select-arrow'>
										<svg width='18' height='18' viewBox='0 0 20 20' fill='none'>
											<path
												d='M5 7.5L10 12.5L15 7.5'
												stroke='currentColor'
												strokeWidth='1.7'
												strokeLinecap='round'
												strokeLinejoin='round'
											/>
										</svg>
									</span>
								</div>
								{errors.training && (
									<p className='error-text'>{errors.training}</p>
								)}
							</div>

							<div className='form-group'>
								<input
									type='text'
									placeholder='Nom & Prénom'
									value={form.fullName}
									onChange={(e) => handleChange('fullName', e.target.value)}
									className={`form-control ${errors.fullName ? 'is-error' : ''}`}
								/>
								{errors.fullName && (
									<p className='error-text'>{errors.fullName}</p>
								)}
							</div>

							<div className='form-group'>
								<input
									type='email'
									placeholder='E-mail'
									value={form.email}
									onChange={(e) => handleChange('email', e.target.value)}
									className={`form-control ${errors.email ? 'is-error' : ''}`}
								/>
								{errors.email && <p className='error-text'>{errors.email}</p>}
							</div>

							<div className='form-group'>
								<div
									className={`phone-field ${errors.phone ? 'is-error' : ''}`}>
									<PhoneInput
										international
										defaultCountry='TN'
										countryCallingCodeEditable={false}
										placeholder='Téléphone'
										value={form.phone}
										onChange={(value) => handleChange('phone', value || '')}
										className='phone-input'
									/>
								</div>
								{errors.phone && <p className='error-text'>{errors.phone}</p>}
							</div>

							<div className='form-group'>
								<textarea
									rows={6}
									placeholder='Message'
									value={form.message}
									onChange={(e) => handleChange('message', e.target.value)}
									className='form-control form-textarea'
								/>
							</div>

							<div className='form-group'>
								<div className='captcha-wrapper'>
									<input
										type='text'
										placeholder='Entrez le captcha'
										value={form.captchaInput}
										onChange={(e) =>
											handleChange('captchaInput', e.target.value.toUpperCase())
										}
										className={`form-control captcha-input ${
											errors.captchaInput ? 'is-error' : ''
										}`}
										autoComplete='off'
									/>

									<div className='captcha-code'>{captcha}</div>

									<button
										type='button'
										className='captcha-refresh'
										onClick={refreshCaptcha}
										aria-label='Rafraîchir le captcha'>
										<RefreshCw size={16} />
									</button>
								</div>
								{errors.captchaInput && (
									<p className='error-text'>{errors.captchaInput}</p>
								)}
							</div>

							<div className='form-footer'>
								<div className='form-note'>
									{submitState.message ? (
										<span
											className={
												submitState.type === 'success'
													? 'success-text'
													: 'error-inline-text'
											}>
											{submitState.message}
										</span>
									) : (
										<span>
											Nous vous répondrons dans les plus brefs délais.
										</span>
									)}
								</div>

								<button
									type='submit'
									disabled={isSubmitting}
									className='submit-button'>
									<span className='submit-button-bg'></span>
									<span className='submit-button-glow'></span>
									<span className='submit-button-text'>
										{isSubmitting ? 'Envoi en cours...' : 'Envoyer le message'}
										<Send size={16} />
									</span>
								</button>
							</div>
						</form>
					</div>

					<div className='contact-map-side'>
						<iframe
							title='Google Maps'
							src={mapUrl}
							loading='lazy'
							className='contact-map'
							referrerPolicy='no-referrer-when-downgrade'
						/>

						<div className='contact-info-box'>
							<div className='contact-info-grid'>
								<div className='info-item'>
									<MapPin className='info-icon' size={18} />
									<div>
										<p className='info-title'>Adresse</p>
										<p className='info-text'>Tunis, Tunisie</p>
									</div>
								</div>

								<div className='info-item'>
									<Phone className='info-icon' size={18} />
									<div>
										<p className='info-title'>Téléphone</p>
										<p className='info-text'>(+216) 29 376 774</p>
									</div>
								</div>

								<div className='info-item'>
									<Mail className='info-icon' size={18} />
									<div>
										<p className='info-title'>E-mail</p>
										<p className='info-text'>admission@clevoryacademy.com</p>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</section>
	);
}
